import React from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';
import _isEqual from 'lodash/isEqual';
import dynamic from 'next/dynamic';
import { withRouter } from 'next/router';
import PropTypes from 'prop-types';

import GoogleOneTap from 'components/googleOneTapConnect';
import rbNavClasses from 'components/resumeBuilder/nav/index.module.css';
import { withJobAlertTrigger, withZippiaHistory } from 'hooks';
import {
  AuthMenu,
  Avatar,
  ExploreDropDown,
  ExploreLink,
  Hamburger,
  Link,
  LogOutButton,
} from 'layouts/header';
import Logo from 'layouts/header/Logo';
import { logActivity } from 'services';
import { oneTapGoogleLogin } from 'services/loginService';
import {
  getSavedSubscription,
  pushSubscriptionNotification,
  startPollingForPushSubscriptionChange,
} from 'services/pushNotificationsService';
import { sessionPopupService } from 'services/sessionPopupService';
import { trackUserPageViewActivities } from 'services/userService';
import {
  closeLocationInputModal,
  closePostSignUpPopup,
  dispatchOpenDLPModal,
  dispatchOpenSessionTimerSignupModal,
  dispatchValidateSessionConditionalSignupModal,
  openLoginModal,
  openPostSignUpPopup,
  openSignUpModal,
} from 'store/modals/actions';
import { __updateuser, updateAnonymousUser } from 'store/user/actions';
import { freezeBodyScroll, getCookie, resizeViewHeight, setCookie, unFreezeBodyScroll } from 'utils';
import { gtmLogSignupEvent, logEventGTM } from 'utils/analyticsGTM';

import { SearchBar } from '../../components/searchBar';

import { COMMON_LINKS } from './navigation/data';
import { MobileNavigation } from './navigation/mobile';
import { Navigation, SEONavigation } from './navigation';

import careerMapClasses from '../../css/careerMapOverlay.module.css';
import subheaderStyles from '../../css/subHeader.module.css';
import classes from './index.module.css';

const { handleSessionTimerPopup } = sessionPopupService;

// only load DLP on client after dlp.open becomes true
const DLP = dynamic(() => import('../dlp'), { ssr: false });
const ShareJobDLPPopup = dynamic(() => import('../shareJobDLPPopup'), { ssr: false });
const EasyApplyForm = dynamic(() => import('../easyApplyForm'), { ssr: false });
const AdviceArticlePopup = dynamic(() => import('../adviceArticle'));
const SessionTimerSignupPopup = dynamic(() => import('../sessionTimerSignupPopup'));
const SessionConditionalSignupPopup = dynamic(() => import('../sessionConditionalSignupPopup'));
const EmbedWidgetPopup = dynamic(() => import('../embedWidgetPopup'));
const CollegePopup = dynamic(() => import('../collegePopup'), { ssr: false });
const MoreInfoConfirmDialog = dynamic(() => import('../moreInfoConfirmDialog'));
const RecommendedJobBubble = dynamic(() => import('components/popups/recommendedJobBubble'), {
  ssr: false,
});

/** Pop-ups */
const LoginPopup = dynamic(() => import('components/loginPopup'), {
  ssr: false,
});
const SurveyPopup = dynamic(() => import('components/surveyPopup/SurveyPopup'), { ssr: false });
const LocationInputFieldPopup = dynamic(
  () => import('components/jobs/card/LocationInputFieldPopup'),
  { ssr: false }
);
const PostSignUpFormPopup = dynamic(() => import('components/postSignUpFormPopup'), { ssr: false });
const SignupRecaptchaModal = dynamic(() => import('widgets/recaptcha/signupRecaptchaModal'), {
  ssr: false,
});
const PostApplyUXModal = dynamic(() => import('widgets/dlp/detail/postApplyUX'), { ssr: false });
const GatedSignupPopup = dynamic(() => import('widgets/gatedSignupPopup'), {
  ssr: false,
});

const NativeNotificationPrompt = dynamic(() => import('components/nativeNotificationPrompt'), {
  ssr: false,
});

const RBTemplatesModal = dynamic(() => import('components/resumeBuilder/popup/templates'), {
  ssr: false,
});

const RBDeletePopup = dynamic(() => import('components/resumeBuilder/popup/delete'), {
  ssr: false,
});

const RBSubscriptionModal = dynamic(() => import('components/resumeBuilder/popup/subscription'), {
  ssr: false,
});

const ResetPasswordModal = dynamic(() => import('components/resumeBuilder/popup/resetPassword'), {
  ssr: false,
});

const ResumePreviewModal = dynamic(() => import('components/resumeBuilder/preview/modal'), {
  ssr: false,
});

const JobAlertPopup = dynamic(() => import('components/jobAlertPopup'), { ssr: false });

const RBFormatDrawer = dynamic(() => import('components/resumeBuilder/popup/rbFormatDrawer'), {
  ssr: false,
});

const ChatBot = dynamic(() => import('components/popups/chatbotBubble'), { ssr: false });

const TrackingPixels = dynamic(
  () => import('components/trackingPixels').then((mod) => mod.TrackingPixels),
  { ssr: false }
);

const ExitIntentPopup = dynamic(() => import('components/exitIntentPopup'), { ssr: false });

const EMPLOYER_LINK_TRACKING = '#source=post_job_header_button';

class Header extends React.Component {
  static propTypes = {
    user: PropTypes.any,
    showSubHeader: PropTypes.bool,
    notificationDot: PropTypes.bool,

    router: PropTypes.any.isRequired,
    modals: PropTypes.any,
    openLoginModal: PropTypes.func.isRequired,
    openSignUpModal: PropTypes.func.isRequired,
    desktopDevice: PropTypes.bool,
  };

  static defaultProps = {
    user: null,
    showSubHeader: false,
    showClusterSubheader: false,
    modals: {},
    notificationDot: false,
    desktopDevice: undefined,
  };

  constructor(props) {
    super(props);
    window.userId = (props.user && props.user.userId) || '';
    this.state = {
      headerMenuExpanded: false,
      headerMenuSearchExpanded: false,
      mobileMenuOpenedBefore: false,
      mobileSearchMenuOpenedBefore: false,
      profileMenu: false,
      isOneTapOpen: false,
      delayedNotification: false,
      popupQueue: [],
      showChatBot: false,
    };
  }

  componentDidMount() {
    const {
      router,
      dispatchOpenDLPModal,
      locals,
      criteria,
      dispatchUpdateAnonymousUser,
      dispatchUpdateUser,
      averageClickProb,
    } = this.props;

    this.handleCredentialResponse = async ({ credential }) => {
      try {
        const response = await oneTapGoogleLogin(credential);
        const { user } = response.data;
        const { isNewUser } = user;
        dispatchUpdateUser(user);
        if (user && isNewUser && locals.enablePostSignUpSurvey) {
          gtmLogSignupEvent('Google Identity');
          window?.openPostSignUpPopup({
            survey: '043',
            survey_step: 'step1',
            src: 'googleOneTap',
          });
          return;
        }
        window.location.reload();
      } catch (e) {
        console.error(e);
      }
    };

    const httpActivityPayload = {
      averageClickProb,
      pageJobTitle: criteria?.title || undefined,
      pageCity: criteria?.city || undefined,
      pageState: criteria?.state || undefined,
      pageCategory: window.pageCategory,
    };

    let zippiaUser = this.props.user;

    logActivity(httpActivityPayload)
      .then((userResponse) => {
        const {
          data: { user },
        } = userResponse;
        dispatchUpdateAnonymousUser(user);
        this.setupSessionPopups(user);
        zippiaUser = user;
        if (!window.userId) {
          window.userId = user.userId;
        }
        window.isLoggedIn = user.isLoggedIn || false;
        logEventGTM();
      })
      .catch(() => {
        this.setupSessionPopups();
      })
      .finally(() => {
        if (zippiaUser) startPollingForPushSubscriptionChange(zippiaUser);
        trackUserPageViewActivities(locals.criteria);
      });
    const listingHash = router.query.listingHashFromEmail;
    listingHash &&
      !locals.enableOpenPaidJobsFromEmailSameTab &&
      dispatchOpenDLPModal({ listingHash });
    resizeViewHeight();
    window.addEventListener('resize', resizeViewHeight);
    document.addEventListener('mousedown', this.handleClickOutside);
    this.turnOnNotifications = setTimeout(() => {
      this.setState(() => ({ delayedNotification: true }));
    }, 60000);

    this.setState({
      showOneTapGoogleLogin: true,
    });
  }

  componentDidUpdate(prevProps) {
    const { modals } = this.props;
    const { popupQueue } = this.state;

    // check whether modals changed
    if (!_isEqual(prevProps.modals, modals)) {
      const openModals = [];
      Object.keys(modals).map((modal) => {
        if (modals[modal] && modals[modal].open) {
          openModals.push(modal);
        }
      });
      const difference = openModals.filter((x) => !popupQueue.includes(x));
      this.updateQueu(difference, difference.length < popupQueue.length ? 60000 : 0);
    }
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside);
    clearTimeout(this.turnOnNotifications);
    clearTimeout(this.turnOnQueueNotifications);
  }

  setWrapperRef = (node) => {
    this.wrapperRef = node;
  };

  updateQueu = (listPopups, delay) => {
    this.turnOnQueueNotifications = setTimeout(() => {
      this.setState(() => ({ popupQueue: listPopups }));
    }, delay);
  };

  isPopupInQueue = (popupId) => {
    const { locals } = this.props;

    const { popupQueue } = this.state;
    return (
      popupQueue.length === 0 ||
      (popupQueue.length &&
        popupQueue.indexOf(popupId) > -1 &&
        locals.popupSuppr &&
        !locals.popupSuppr.indexOf(popupId))
    );
  };

  popupRenderPageRestrictions = (popupId) => {
    const { locals } = this.props;
    return locals.popupSuppr && locals.popupSuppr.indexOf(popupId);
  };

  handleClickOutside = (event) => {
    if (this.wrapperRef && !this.wrapperRef.contains(event.target)) {
      this.setState({ profileMenu: false });
    }
  };

  setupSessionPopups = (user = {}) => {
    const {
      locals,
      modals: { sessionTimerSignup },
      dispatchOpenSessionTimerSignupModal,
      dispatchValidateSessionConditionalSignupModal,
      hidePersonalizedJobs = false,
    } = this.props;

    if (!hidePersonalizedJobs) {
      handleSessionTimerPopup(
        user,
        locals,
        sessionTimerSignup,
        dispatchOpenSessionTimerSignupModal
      );
    }

    dispatchValidateSessionConditionalSignupModal();
  };

  toggleMobileMenu = (event) => {
    if (!event) return;

    const { headerMenuExpanded, mobileMenuOpenedBefore } = this.state;

    if (!headerMenuExpanded && !mobileMenuOpenedBefore) {
      this.setState({ mobileMenuOpenedBefore: true });
    }
    this.setState({ headerMenuSearchExpanded: false });

    if (headerMenuExpanded) {
      unFreezeBodyScroll();
    } else {
      freezeBodyScroll();
    }

    this.setState({ headerMenuExpanded: !headerMenuExpanded });
  };

  toggleMobileSearchMenu = () => {
    const { headerMenuSearchExpanded, mobileSearchMenuOpenedBefore } = this.state;

    if (!headerMenuSearchExpanded && !mobileSearchMenuOpenedBefore) {
      this.setState({ mobileSearchMenuOpenedBefore: true });
    }
    this.setState({ headerMenuExpanded: false });
    this.setState({ headerMenuSearchExpanded: !headerMenuSearchExpanded });
  };

  toggleProfileMenu = () => {
    const { profileMenu } = this.state;
    this.setState({ profileMenu: !profileMenu });
  };

  hideProfileMenu = () => {
    const { profileMenu } = this.state;
    if (profileMenu) {
      this.setState({ profileMenu: false });
    }
  };

  goToMatches = () => {
    window.location.href = '/matches/';
  };

  signUpPopup = () => {
    const { zippiaHistory, openSignUpModal } = this.props;
    const surveyStepSituation = getCookie('_zippia-surveyStepSituation');
    const survey = surveyStepSituation === 'groupB' ? '040' : '036';
    const query = {
      survey_step: 'step1',
      survey,
      utm_campaign: 'nav_sign_up',
    };
    openSignUpModal({
      ...query,
      popupType: 'header-sign-up',
    });
    zippiaHistory.pushQuery(query);
  };

  renderExploreLink = (className = '', logged = false) => {
    const { router } = this.props;
    const { profileMenu } = this.state;

    return (
      <div
        className={classnames('position-relative', classes.discoverMenu, {
          'd-none d-lg-block': !logged,
        })}
      >
        <ExploreLink isActive={router.asPath.includes('/explore')} className={className} />
        <ExploreDropDown exploreDropDownVisible={!profileMenu} />
      </div>
    );
  };

  renderMobileExploreList = () => {
    const list = [
      { href: '/careers/', title: 'By Job Titles' },
      { href: '/colleges-and-majors/', title: 'By College Majors' },
      { href: '/company/', title: 'By Companies' },
      { href: '/jobs/', title: 'Jobs Near Me' },
      { href: '/advice/', title: 'Job Search Advice' },
      { href: '/salaries/', title: 'Salaries' },
    ];
    return <ul>{list.map((link) => this.renderLinkWithLi(link, true))}</ul>;
  };

  /**
   * @param {object} link link options
   * @param {string} link.title title of the link
   * @param {string} link.href url
   */
  renderLinkWithLi = (link) => (
    <li key={link.title}>
      <a href={link.href} className="d-flex align-items-center justify-content-between">
        {link.title}
        <i
          className={classnames('fa', classes.faChevronRight, classes.subFaChevronRight)}
          aria-hidden="true"
        />
      </a>
    </li>
  );

  handleLocationInputPopupClose = () => {
    this.props.closeLocationInputModal();
  };

  handlePostSignUpPopupClose = () => {
    this.props.closePostSignUpPopup();
    window.location.reload();
  };

  renderOneTapGoogleLogin() {
    const { openSignUpModal, locals } = this.props;
    const { googleOneTapClientId } = locals;
    window.openPostSignUpPopup = openSignUpModal;
    const style = {
      position: 'fixed',
      top: '52px',
      right: '10px',
      zIndex: 999999,
      display: !this.props.modals.login.open ? 'block' : 'none',
    };
    return (
      <GoogleOneTap
        style={style}
        showDisplayOr={false}
        googleClientId={googleOneTapClientId}
        onGoogleOneTap={this.handleCredentialResponse}
      />
    );
  }

  onSignIn = () => {
    const { openLoginModal } = this.props;
    const width = window?.innerWidth;

    if (width < 992) {
      this.toggleMobileMenu();
    }

    openLoginModal();
  };

  handleLogout = () => {
    // Unsubscribe to the Push notification
    const { user } = this.props;
    // Delete the cookie
    setCookie({
      item: '_zippia-push_sub',
      value: Date.now(),
      expiredays: 0,
      path: '/',
    });
    const subscription = getSavedSubscription();

    if (subscription) {
      // Make unsubscribe call for push signed in user
      const userId = subscription?.userId ?? user?.userId;
      if (userId) pushSubscriptionNotification(userId, 'unsubscribe', subscription, false);
    }
    window.location.href = '/auth/logout';
  };

  renderNavigation = (userLoggedIn, isCompanyUser) => {
    const { user, router, desktopDevice } = this.props;

    const { headerMenuExpanded, profileMenu } = this.state;

    return (
      <div className="d-flex align-items-center">
        <div className={classes.options}>
          <ul
            className={classnames(
              'nav navbar-nav header links flex-row mt-0 flex-nowrap',
              classes.nav
            )}
          >
            {isCompanyUser && <Link
              title="Company Profile"
              href={`/employer/profile/${user?.employerData?.companyID}/`}
              isActive={router.asPath?.includes('/employer/profile/')}
            />}
            <Link
              title="Post Job"
              href={`/employer/${EMPLOYER_LINK_TRACKING}`}
              isActive={router.asPath?.includes('/employer/')}
            />
          </ul>
        </div>
        {userLoggedIn ? (
          <>
            {!this.hideSearchBar() && (
              <i
                className="fa fa-search white-text mr-24 d-block d-lg-none"
                onClick={this.toggleMobileSearchMenu}
              ></i>
            )}

            <Avatar
              avatar={user?.photo}
              desktopDevice={desktopDevice}
              toggleProfileMenu={this.toggleProfileMenu}
              toggleMobileMenu={this.toggleMobileMenu}
              isCompanyUser={isCompanyUser}
              goToMatches={this.goToMatches}
              handleLogout={this.handleLogout}
              profileMenu={profileMenu}
              setWrapperRef={this.setWrapperRef}
            />
          </>
        ) : (
          <>
            <div className="ml-0">
              <div onClick={this.onSignIn} className={classnames('d-block d-lg-none white-text z-mr-20 font-14 text-uppercase cursor-pointer', classes.mobileSignInBtn)}>
                Sign In
              </div>
              <AuthMenu onSignIn={this.onSignIn} />
            </div>
            {!this.hideSearchBar() && (
              <i
                className="fa fa-search white-text mr-24 d-block d-lg-none"
                onClick={this.toggleMobileSearchMenu}
              ></i>
            )}
            <Hamburger menuExpanded={headerMenuExpanded} onClick={this.toggleMobileMenu} />
          </>
        )}
      </div>
    );
  };

  renderMobileNav = (userLoggedIn, isCompanyUser, companyID) => {
    const getLinks = (options = {}) => {
      const { isCompleted } = options;

      const links = [];

      if (userLoggedIn) {
        if (isCompanyUser) {
          links.push({ text: 'Company Profile', href: `/employer/profile/${companyID}/`});
        }
        links.push(...COMMON_LINKS({ isLoggedIn: userLoggedIn && isCompleted }))

        links.push({ text: 'Preferences', href: '/profile/jobs/' });
      }

      return links;
    }

    return (
      <MobileNavigation getCommonLinks={getLinks} onLinkClick={this.toggleMobileMenu} />
    )
  };

  hideSearchBar = () => {
    const searchBarExcludedURLList = ['/matches/', '/resume-builder'];

    return searchBarExcludedURLList.find((x) => {
      return this.props?.router?.asPath.includes(x);
    });
  };

  handleNoJob = () => {
    this.setState({ showChatBot: true });
  };

  getShowSponsoredJobPopout = () => {
    const { showSponsoredJobs, showEmployerPopout, criteria } = this.props;
    if (typeof showSponsoredJobs !== 'boolean' && typeof showEmployerPopout !== 'boolean') {
      return !!criteria?.title;
    }
    return !!showSponsoredJobs || !!showEmployerPopout;
  }

  render() {
    const {
      user,
      showSubHeader,
      showClusterSubheader,
      modals,
      popupDetails,
      resumeBuilder,
      jobAlertProps,
      criteria,
      showEmployerPopout = false,
      isCareerDescriptionPage,
      isCareerHowToHirePage,
    } = this.props;

    const isLocationInputPopupOpen = modals.locationInputPopup.open;
    const isPostSignUpPopupOpen = modals.postSignUpSurveyPopup.open;
    const locationInputPurpose = modals.locationInputPopup.purpose;
    const {
      headerMenuExpanded,
      headerMenuSearchExpanded,
      showOneTapGoogleLogin,
      mobileMenuOpenedBefore,
      mobileSearchMenuOpenedBefore,
      showChatBot,
    } = this.state;

    const userLoggedIn = user && (user.isLoggedIn || user.emailAddr);
    const isCompanyUser =
      userLoggedIn &&
      !!user.employerData &&
      (!!user.employerData.companyID || !!user.employerData.originalCompanyName);

    const {
      flow: { currentStepIndex },
      resumeBuilderState,
    } = resumeBuilder;

    const showHeader = !!(
      currentStepIndex > 2 &&
      currentStepIndex !== 10 &&
      resumeBuilderState[currentStepIndex].navLabel
    );

    if (showHeader) {
      return null;
    }

    const showSponsored = this.getShowSponsoredJobPopout();

    const getRecommendedBubbleSrc = () => {
      if (isCareerDescriptionPage) {
        return '#src=job-description-callout-emp4014-b30';
      }
      if (isCareerHowToHirePage) {
        return '#src=hth-callout-emp4014-b50';
      }

      return '';
    };

    const isChatbotPage = [
      this.props.isCareerSkillsPage,
      this.props.isCareerWhatTheyDoPage,
      this.props.isCareerEducationPage,
      this.props.isCompanyLocationsPage,
    ].some(Boolean);

    return (
      <header
        className={classnames('header header-new w-100', classes.header, {
          [classes.withClusterHeader]: showClusterSubheader,
          [subheaderStyles['with-subheader']]: showSubHeader,
          'without-subheader': !showSubHeader,
        })}
      >
        <nav className={classnames('navbar p-0', classes.navbar)}>
          <div
            className={classnames(
              'container d-flex justify-content-between flex-nowrap',
              classes.container,
              {
                'align-items-center': showHeader,
                [rbNavClasses['rb-header']]: showHeader,
              }
            )}
          >
            <div className="d-flex align-items-center">
              <Logo userLoggedIn={userLoggedIn} showSmallLogo={userLoggedIn} />
              {!showHeader && !isCompanyUser && !this.hideSearchBar() && (
                <div className="d-none d-lg-block z-ml-20">
                  <SearchBar inputFieldClasses="z-px-16 z-font-15 z-text-base" newDesign />
                </div>
              )}
              <Navigation />
            </div>
            {!showHeader && this.renderNavigation(userLoggedIn, isCompanyUser)}
          </div>
        </nav>

        {!showHeader && (
          <>
            {/* Mobile Drop Down menu */}
            <div
              className={classnames(classes.mobileMenu, {
                [classes.showMobileMenu]: headerMenuExpanded,
                [classes.hideMobileMenu]: !headerMenuExpanded && mobileMenuOpenedBefore,
              })}
            >
              <div className={classes.mobileNavigation}>
                {this.renderMobileNav(userLoggedIn, isCompanyUser, user?.employerData?.companyID)}
                <div className={classes.mobileSignBtns}>
                  <a className={classnames('d-block z-br-4 z-py-8 text-center w-100 text-uppercase z-font-base z-leading-20px', classes.postJobMobile)} href={`/employer/${EMPLOYER_LINK_TRACKING}`}>Post Job</a>
                  {userLoggedIn ? (
                    <LogOutButton onClick={this.handleLogout} className="w-100" />
                  ) : (
                    <AuthMenu onSignIn={this.onSignIn} showMobile />
                  )}
                </div>
              </div>
            </div>

            {/* disable chrome autoprefill */}
            <form>
              <input
                type="text"
                id="username"
                autoComplete="off"
                style={{
                  width: 0,
                  height: 0,
                  visibility: 'hidden',
                  position: 'absolute',
                  left: 0,
                  top: 0,
                }}
              />
              <input
                type="password"
                autoComplete="off"
                style={{
                  width: 0,
                  height: 0,
                  visibility: 'hidden',
                  position: 'absolute',
                  left: 0,
                  top: 0,
                }}
              />
            </form>
            <div
              className={classnames(classes.mobileMenu, {
                [classes.showMobileMenu]: headerMenuSearchExpanded,
                [classes.hideMobileMenu]: !headerMenuSearchExpanded && mobileSearchMenuOpenedBefore,
              })}
            >
              <SearchBar />
            </div>
          </>
        )}

        <LoginPopup openSignUpPopup={this.signUpPopup} popupDetails={popupDetails} />
        {/* signup modal popup */}
        {(modals.signUp.open || isLocationInputPopupOpen) &&
          !modals.signUp.surveyFullPage &&
          !modals.signUp.oneTapSignUp && (
            <div
              className={`${careerMapClasses['career-map-overlay']} ${
                modals.postApplyUX.open ? 'post-apply-ux-overlay' : ''
              }`}
            />
          )}
        <SurveyPopup />

        {!userLoggedIn && showOneTapGoogleLogin && !this.popupRenderPageRestrictions('googleSignOn')
          ? this.renderOneTapGoogleLogin()
          : null}

        {/* Location Input Popup will be displayed when user is not signed in
                and try to save/dismiss the job he/she is interested in */}
        <LocationInputFieldPopup
          purpose={locationInputPurpose}
          open={isLocationInputPopupOpen}
          onCloseModel={this.handleLocationInputPopupClose}
        />

        <PostSignUpFormPopup
          open={isPostSignUpPopupOpen}
          onCloseModel={this.handlePostSignUpPopupClose}
        />

        {/* DLP as popup, open only if dlp as isPopUp */}
        {modals.dlp.open && !(modals.login.open || modals.signUp.open) && modals.dlp.isPopUp && (
          <DLP />
        )}

        {modals.postApplyUX.open && <PostApplyUXModal />}

        {modals.share.open && modals.share.type === 'job' && <ShareJobDLPPopup />}

        {modals.easyApply.open && <EasyApplyForm />}

        {modals.adviceArticlePopup.articlePopupOpen && <AdviceArticlePopup />}

        {modals.sessionTimerSignup.open && !modals.signUp?.open && !modals.gatedSignup?.open && (
          <SessionTimerSignupPopup />
        )}

        <NativeNotificationPrompt />

        {modals.sessionConditionalSignup.open && <SessionConditionalSignupPopup />}

        {modals.embedWidget.open && <EmbedWidgetPopup />}

        {modals.signupRecaptcha.open && <SignupRecaptchaModal />}

        {modals.collegePopup.open && <CollegePopup />}

        {modals.moreInfoModal.open && <MoreInfoConfirmDialog />}

        {modals.resumeBuilderModals.templates && <RBTemplatesModal />}

        {modals.resumeBuilderModals.delete && <RBDeletePopup />}

        {modals.resumeBuilderModals.subscription && <RBSubscriptionModal />}

        {modals.resumeBuilderModals.resetPassword && <ResetPasswordModal />}

        {resumeBuilder.previewModalInfo.open && <ResumePreviewModal />}

        {modals.jobAlert.open && <JobAlertPopup {...jobAlertProps} />}

        {window?.location?.pathname !== '/resume-builder/' && window?.userId && (
          <GatedSignupPopup />
        )}

        <ExitIntentPopup
          location={[
            {
              city: criteria?.geoCity || '',
              state: (criteria?.geoCity ? criteria?.geoStateCode : criteria?.geoState) || '',
            },
          ]}
        />

        {modals.rbFormatDrawer.open && <RBFormatDrawer />}

        {!!showSponsored && (
          <RecommendedJobBubble
            isEmployerPopout={showEmployerPopout}
            criteria={criteria}
            user={user}
            onNoJob={this.handleNoJob}
            src={getRecommendedBubbleSrc()}
          />
        )}

        {isChatbotPage && (showChatBot || !showSponsored) && <ChatBot jobTitle={criteria.title} />}

        <TrackingPixels />

        <SEONavigation />
      </header>
    );
  }
}
const mapDispatchToProps = {
  openLoginModal,
  openSignUpModal,
  dispatchOpenDLPModal,
  closeLocationInputModal,
  dispatchOpenSessionTimerSignupModal,
  dispatchValidateSessionConditionalSignupModal,
  closePostSignUpPopup,
  openPostSignUpPopup,
  dispatchUpdateAnonymousUser: updateAnonymousUser,
  dispatchUpdateUser: __updateuser,
};

export default withJobAlertTrigger(
  withZippiaHistory(
    withRouter(
      connect(
        ({ user, locals, modals, resumeBuilder }) => ({
          user,
          locals,
          modals,
          popupDetails: modals.login,
          resumeBuilder,
        }),
        mapDispatchToProps
      )(Header)
    )
  )
);
